import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  JobStakeholderRole,
  JobStakeholderRoleTranslation,
  JobStakeholdersPopupType,
} from 'src/components/JobForm/constants';
import { JobStakeholdersPopupComponentDefaultProps, JobStakeholdersPopupComponentPropTypes } from 'src/components/JobStakeholdersPopup/propTypes';
import { noop } from 'src/utils/core/event';
import { Popup } from 'src/components/Popup';
import { useCapManagers } from 'src/hooks/capManagers/useCapManagers';
import { useCreateJobStakeholder } from 'src/hooks/jobStakeholder/useCreateJobStakeholder';
import { useUpdateJobStakeholder } from 'src/hooks/jobStakeholder/useUpdateJobStakeholder';
import FormRow from 'src/components/Form/FormRow';
import s from 'src/components/JobForm/JobForm.styles';
import SelectBox from 'src/components/SelectBox';
import Styles from 'src/components/JobStakeholdersPopup/JobStakeholdersPopup.styles';

const JobStakeholdersPopupComponent = ({
  disabled,
  excludedUsersIds,
  jobId,
  jobStakeholder,
  onClose,
  onSubmit,
  type,
}) => {
  const { t } = useTranslation();

  const stakeholderRoles = useMemo(() => Object.values(JobStakeholderRole).map(role => ({
    label: t(JobStakeholderRoleTranslation[role]),
    value: role
  })), []);

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserRole, setSelectedUserRole] = useState(null);

  useEffect(() => {
    if (jobStakeholder) {
      setSelectedUserRole(stakeholderRoles.find(role => role.value === jobStakeholder.role));
    }
  }, [jobStakeholder]);

  let capManagers = null;
  let isFetchingCapManagers = false;
  let isCreatingJobStakeholder = false;
  let isUpdatingJobStakeholder = false;
  let createJobStakeholder = noop();
  let updateJobStakeholder = noop();

  if (type === JobStakeholdersPopupType.Add) {
    {
      const { data, loading } = useCapManagers(jobId);

      capManagers = data?.capManagers?.filter(manager => !excludedUsersIds.includes(parseInt(manager.id, 10))) || [];
      isFetchingCapManagers = loading;
    }
    {
      const {
        isLoading,
        createJobStakeholderCallback,
      } = useCreateJobStakeholder();

      isCreatingJobStakeholder = isLoading;
      createJobStakeholder = createJobStakeholderCallback;
    }
  } else if (type === JobStakeholdersPopupType.Edit) {
    const {
      isLoading,
      updateJobStakeholderCallback,
    } = useUpdateJobStakeholder();

    isUpdatingJobStakeholder = isLoading;
    updateJobStakeholder = updateJobStakeholderCallback;
  }

  const isLoading = isFetchingCapManagers || isCreatingJobStakeholder || isUpdatingJobStakeholder;

  const onSubmitPopup = async () => {
    let response = null;

    if (type === JobStakeholdersPopupType.Add) {
      response = await createJobStakeholder({
        jobId,
        role: selectedUserRole.value,
        userId: selectedUser.value,
      });
    } else if (type === JobStakeholdersPopupType.Edit) {
      response = await updateJobStakeholder({
        id: jobStakeholder.id,
        role: selectedUserRole.value,
      });
    }

    if (response) {
      await onSubmit();
      onClose();
    }
  };

  return (
    <Popup
      onClose={onClose}
      title={<s.Title>{type === JobStakeholdersPopupType.Add ? t('ADD_USER') : t('EDIT_USER')}</s.Title>}
      withOverflow
    >
      {
        <FormRow inline spaced>
          {type === JobStakeholdersPopupType.Add && (
            <>
              <Styles.RowWrapper>
                <Styles.RowLabel>{t('ROLE')}</Styles.RowLabel>
                <SelectBox
                  disabled={isLoading || !capManagers || disabled}
                  onChange={v => setSelectedUserRole(v)}
                  options={stakeholderRoles}
                  value={selectedUserRole || null}
                />
              </Styles.RowWrapper>
              <Styles.RowWrapper>
                <Styles.RowLabel>{t('USER')}</Styles.RowLabel>
                <SelectBox
                  disabled={isLoading || !capManagers || disabled}
                  onChange={v => setSelectedUser(v)}
                  options={capManagers ? capManagers
                    .filter(user => user.active)
                    .sort((a, b) => (a.firstName > b.firstName) ? 1 : -1)
                    .map(user => ({
                      label: `${user.firstName} ${user.lastName} (${user.email})`,
                      value: parseInt(user.id, 10)
                    })) : []
                  }
                  value={selectedUser || null}
                />
              </Styles.RowWrapper>
            </>
          )}
          {type === JobStakeholdersPopupType.Edit && (
            <>
              <Styles.RowWrapper>
                <Styles.RowLabel>{t('USER')}</Styles.RowLabel>
                <Styles.RowWithText>
                  {`${jobStakeholder.user.firstName} ${jobStakeholder.user.lastName} (${jobStakeholder.user.email})`}
                </Styles.RowWithText>
              </Styles.RowWrapper>
              <Styles.RowWrapper>
                <Styles.RowLabel>{t('ROLE')}</Styles.RowLabel>
                <SelectBox
                  disabled={isLoading || disabled}
                  onChange={v => setSelectedUserRole(v)}
                  options={stakeholderRoles}
                  value={selectedUserRole || null}
                />
              </Styles.RowWrapper>
            </>
          )}
          <s.SubmitButton
            disabled={isLoading ||
              (type === JobStakeholdersPopupType.Add && !selectedUser) ||
              !selectedUserRole ||
              disabled
            }
            onClick={onSubmitPopup}
            orange
            sm
            solid
          >
            {t('OK')}
          </s.SubmitButton>
          <s.LinkButton
            disabled={isLoading || disabled}
            ghost
            onClick={onClose}
            red
            sm
            type="button"
          >
            {t('CANCEL')}
          </s.LinkButton>
        </FormRow>
      }
    </Popup>
  );
};

JobStakeholdersPopupComponent.propTypes = JobStakeholdersPopupComponentPropTypes;
JobStakeholdersPopupComponent.defaultProps = JobStakeholdersPopupComponentDefaultProps;

export const JobStakeholdersPopup = memo(JobStakeholdersPopupComponent);
